import { Injectable, OnDestroy } from "@angular/core";
import { CoreLogService } from "../../services/log.service";
import { WebShopService } from "../../services/webShop.service";
import { CouponDataModel } from "../../shared/models";
import { ContentType, ImageOrientation } from "../../shared/contentTypes";
import { BehaviorSubject, Observable } from "rxjs";
import { IAppCoupons, ICouponRedeemService } from "./couponRedeem.facade";
import { UserHelperService } from "../../services/userHelper.service";
import Utils from "../../shared/utils";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { CouponRedemptionService } from "../../services/couponRedemption.service";
import * as moment from "moment";

export class AppCoupon implements IAppCoupons {
  public get id(): number {
    return this._model.id;
  }
  public get hasImage(): boolean {
    return this._model.last_image_update_at_ymd > 0;
  }

  public get couponCode(): string {
    return this._model.qr_code_text;
  }

  public get anonymCode(): boolean {
    return this._model.anonym_code;
  }

  public get isRedeemed(): boolean {
    const me = this;
    return me._couponRedemptionService.isCouponRedeemed(me._model.id);
  }

  /** Guards null and undefined; portal flag RedeemOncePerUser. */
  public get isRedeemOncePerUser(): boolean {
    const me = this;
    return me._model.redeem_once_per_user != null ? me._model.redeem_once_per_user : false;
  }

  public get image(): string {
    if (this.hasImage)
      return ContentType.Coupon + "/" + this._model.id + "_photo_" + ImageOrientation.Landscape + "/" + this._model.last_image_update_at_ymd + "/" + this._model.last_image_update_at_hms;
    else
      return "";
  }

  private _model: CouponDataModel

  public constructor(model: CouponDataModel, private _couponRedemptionService: CouponRedemptionService) {
    const me = this;
    me._model = model;
  }
}

@Injectable()
export class CouponRedeemService implements ICouponRedeemService, OnDestroy {

  private _appCouponObsServer: IObjectObserver<CouponDataModel>;

  constructor(private _webShopService: WebShopService
    , private _coreLogService: CoreLogService
    , private _userHelperService: UserHelperService
    , private _couponRedemptionService: CouponRedemptionService
    , private _objObsInjectHelper: CircularInjectionHelper) {
    const me = this;
    me._appCouponObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<CouponDataModel>(ContentType.Coupon, me._objObsInjectHelper);
  }

  ngOnDestroy(): void {
    const me = this;
  }

  private _useScan: boolean;
  public get useScan(): boolean {
    const me = this;
    return me._useScan;
  }

  private _requiredCouponLength: number;
  public get requiredCouponLength(): number {
    const me = this;
    return me._requiredCouponLength;
  }

  public async getUseScanCoupon() {
    const me = this;
    const settings = await me._webShopService.getWebshopSettingsDataModel();
    if (settings) {
      me._useScan = settings.scan_coupon_code;
    }
    return me._useScan;
  }

  public async getRequiredLength() {
    const me = this;
    const settings = await me._webShopService.getWebshopSettingsDataModel();
    if (settings) {
      me._requiredCouponLength = settings.coupon_code_length;
    }
    return me._requiredCouponLength;
  }

  private _isAppCouponActive: boolean = true;
  public get isAppCouponActive(): boolean {
    const me = this;
    me._isAppCouponActive = me._webShopService.canNavigateToCoupons;
    return me._isAppCouponActive;
  }

  private _appCoupons: AppCoupon[] = [];
  private _appCouponsSubject: BehaviorSubject<ReadonlyArray<IAppCoupons>> = new BehaviorSubject<ReadonlyArray<IAppCoupons>>([]);
  public get appCoupons$(): Observable<ReadonlyArray<IAppCoupons>> {
    return this._appCouponsSubject.asObservable();
  }

  public loadAppCoupons(): Promise<void> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<void>(async (resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      const selectedLocationId = me._webShopService.getServiceLocation();
      const currentDate: moment.Moment = moment();
      try {
        const appCoupons = await me._appCouponObsServer.GetObjectsByFilter((model) => !model.deleted
          && model.release
          && (model.public || isTestUser)
          && model.locations.includes(selectedLocationId)
          && model.end_date && currentDate.startOf("day").valueOf() <= model.end_date.valueOf());
        let appCouponsList = [];
        if (appCoupons.length > 0) {
          await me._couponRedemptionService.ensureRedeemedCouponsLoaded();
          appCoupons.sort((a, b) => b.date_ymd - a.date_ymd).forEach(coupon => {
            appCouponsList.push(new AppCoupon(coupon, me._couponRedemptionService));
          });
        }
        if (!Utils.IsEqual(me._appCoupons, appCouponsList)) {
          me._appCoupons = appCouponsList;
          me._appCouponsSubject.next(me._appCoupons);
        }
        resolve();
      } catch (error) {
        me._coreLogService.error("CouponRedeemService.loadAppCoupons", "catch", error);
        reject();
      }
    });
  }
}
